import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { Loader2, CheckCircle2, KeyRound } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    (async () => {
      try {
        const { data } = await axios.get(`${API}/auth/invite-preview/${token}`);
        setPreview(data);
      } catch {
        setPreview({ valid: false, reason: "not_found" });
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) return toast.error("Password must be at least 8 characters");
    if (pw !== pw2) return toast.error("Passwords don't match");
    setBusy(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password: pw });
      setDone(true);
      toast.success("Password set — you can now sign in.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to set password");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F4EE]">
        <Loader2 className="w-8 h-8 animate-spin text-[#E24E1B]" />
      </div>
    );
  }
  if (!preview?.valid) {
    const reason = {
      not_found: "This link isn't valid. Ask your admin for a fresh invite.",
      already_used: "This link has already been used. Sign in normally, or ask your admin to send a new one.",
      expired: "This link has expired. Ask your admin for a fresh invite.",
    }[preview?.reason] || "Invalid link.";
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F4EE] p-8">
        <div className="max-w-md text-center bg-white border border-neutral-200 p-8" data-testid="reset-password-invalid">
          <h1 className="font-display text-3xl font-bold mb-3">Link unavailable</h1>
          <p className="text-neutral-600 mb-4">{reason}</p>
          <Link to="/login" className="lf-btn-outline inline-block">Go to sign-in</Link>
        </div>
      </div>
    );
  }
  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F4EE] p-8">
        <div className="max-w-md text-center bg-white border border-neutral-200 p-8" data-testid="reset-password-done">
          <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto mb-3" />
          <h1 className="font-display text-3xl font-bold mb-3">All set</h1>
          <p className="text-neutral-600 mb-4">Your password is ready. Sign in with <strong>{preview.email}</strong> and the password you just chose.</p>
          <Link to="/login" className="lf-btn-primary inline-block">Sign in →</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F7F4EE] p-8" data-testid="reset-password-page">
      <Toaster position="top-right" richColors />
      <form onSubmit={submit} className="max-w-md w-full bg-white border border-neutral-200 p-8 space-y-4">
        <div className="text-center mb-2">
          <KeyRound className="w-8 h-8 text-[#E24E1B] mx-auto mb-2" />
          <h1 className="font-display text-3xl font-bold">Set your password</h1>
          <p className="text-neutral-600 text-sm mt-1">for <strong>{preview.email}</strong></p>
        </div>
        <div>
          <label className="overline block mb-2">New password (min 8 characters)</label>
          <input type="password" required minLength={8} value={pw}
                 onChange={(e) => setPw(e.target.value)}
                 data-testid="reset-pw-input" className="lf-input" autoFocus />
        </div>
        <div>
          <label className="overline block mb-2">Confirm password</label>
          <input type="password" required minLength={8} value={pw2}
                 onChange={(e) => setPw2(e.target.value)}
                 data-testid="reset-pw-confirm" className="lf-input" />
        </div>
        <button type="submit" disabled={busy} data-testid="reset-pw-submit" className="lf-btn-primary w-full">
          {busy ? "Setting password…" : "Set password & continue"}
        </button>
      </form>
    </div>
  );
}
